import React from 'react'
import styled from '@emotion/styled'
import dayjs from 'dayjs'
import { bool, string } from 'prop-types'

import { Link } from '@jelly/ui'
import { stripOutHtml } from '@jelly/utils'
import { Thumbnail } from '../Thumbnail'

const CardTitle = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	display: inline-block;
	color: ${({ theme }) => theme.colors.general};
	font-size: 1.3rem;
	word-break: break-word;
`
const PrependTitle = styled(CardTitle)`
	margin-right: 5px;
`
const CardWrapper = styled(Link)`
	${({ theme }) => theme.fonts.primary};
	display: flex;
	flex-direction: row;
	border: solid 2px ${({ theme }) => theme.colors.gray[200]};
	border-radius: 5px;
	box-shadow: 0 6px 8px 0 rgba(31, 70, 87, 0.04);
	margin: 1rem 0;
	padding: 1.5rem 1.5rem 2.25rem 1.5rem;

	&:hover {
		${CardTitle} {
			color: ${({ theme }) => theme.colors.secondary};
		}
		box-shadow: 0 3px 8px 0 rgba(31, 70, 87, 0.02);
	}
`
const CardContext = styled.div`
	margin-left: 1rem;
`
const Label = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	color: ${({ theme }) => theme.colors.tertiary};
	font-size: 12px;
	text-transform: capitalize;
`
const CardDescription = styled.div`
	color: ${({ theme }) => theme.colors.general};
	margin-top: 0.5rem;
	word-break: break-word;
`
const CardDate = styled.div`
	color: ${({ theme }) => theme.colors.gray[400]};
	font-size: 14px;
	margin-top: 1rem;
`

ActivityCard.propTypes = {
	avatar: string,
	canonical: string,
	description: string,
	date: string,
	isUpdated: bool,
	label: string,
	noFollow: bool,
	prependTitle: string,
	thumbnail: string,
	title: string,
}

/**
 * Clickable Card that displays Content with optional fields
 * - Avatar or Thumbnail
 * - Byline
 * - Description
 * - Date
 * - Label
 *
 * @param {String} props.avatar :: src of the avatar picture
 * @param {String} props.canonical :: url destination where card should direct the user to
 * @param {String} props.description :: description text
 * @param {String} props.date :: date to be displayed within card
 * @param {Boolean} props.isUpdated :: boolean check to see if content has been modified
 * @param {String} props.label :: text to appear above title
 * @param {Boolean} props.noFollow :: add a rel = 'nofollow noopener noreferrer' attribute to the card link
 * @param {String} props.prependTitle :: text to prepend the title with (no hover state)
 * @param {String} props.thumbnail :: thumbnail source for regular square thumbnails
 * @param {String} props.title :: title of the card
 */
export function ActivityCard({
	avatar,
	canonical,
	description,
	date,
	isUpdated,
	label,
	noFollow = false,
	prependTitle,
	thumbnail,
	title,
}) {
	return (
		<CardWrapper href={canonical} {...(noFollow && { rel: 'nofollow noopener noreferrer' })}>
			<Thumbnail {...(avatar && { variant: 'avatar' })} alt={`${title} - thumbnail`} src={avatar || thumbnail} />
			<CardContext>
				{label && <Label>{label?.replace('-', ' ')}</Label>}
				{title && (
					<CardTitle>
						{prependTitle && <PrependTitle>{prependTitle}</PrependTitle>} {stripOutHtml(title)}
					</CardTitle>
				)}
				{description && <CardDescription>{stripOutHtml(description)}</CardDescription>}
				{date && (
					<CardDate>
						{isUpdated && 'Updated'} {dayjs(date).format('MMMM DD, YYYY')}
					</CardDate>
				)}
			</CardContext>
		</CardWrapper>
	)
}
